import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { api } from "@/lib/api";

const ACTIVITY = [
  ["sedentary", "Desk job, little exercise"],
  ["light", "Light exercise 1-3 days"],
  ["moderate", "Moderate 3-5 days"],
  ["active", "Hard exercise 6-7 days"],
  ["athlete", "Athlete / physical job"],
];

export const ProfileDialog = ({ open, onOpenChange, profile, onSaved }) => {
  const [form, setForm] = useState({
    age: "", sex: "female", height_cm: "", weight_kg: "",
    activity_level: "moderate", reminders_enabled: true,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      age: String(profile?.age ?? ""),
      sex: profile?.sex ?? "female",
      height_cm: String(profile?.height_cm ?? ""),
      weight_kg: String(profile?.weight_kg ?? ""),
      activity_level: profile?.activity_level ?? "moderate",
      reminders_enabled: profile?.reminders_enabled ?? true,
    });
  }, [open, profile]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/profile", {
        age: Number(form.age),
        sex: form.sex,
        height_cm: Number(form.height_cm),
        weight_kg: Number(form.weight_kg),
        activity_level: form.activity_level,
        reminders_enabled: form.reminders_enabled,
      });
      toast.success(`Daily target set to ${data.goals.calories} kcal`);
      onSaved(data);
      onOpenChange(false);
    } catch (e2) {
      toast.error(e2?.response?.data?.detail?.[0]?.msg || "Could not save your details");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-sand border-[#E8E3D9] sm:max-w-lg max-h-[92vh] overflow-y-auto"
        data-testid="profile-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-deep-olive tracking-tight">
            Your daily calorie target
          </DialogTitle>
          <DialogDescription className="text-sm text-stone-grey">
            Age, height, weight and activity decide how much you should eat each day.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <p className="text-sm text-stone-grey leading-relaxed">
            We use the Mifflin-St Jeor formula on your age, height, weight, sex and activity to
            work out how much you should eat each day.
          </p>

          <div className="grid grid-cols-3 gap-3">
            {[
              ["age", "Age", "profile-age-input"],
              ["height_cm", "Height (cm)", "profile-height-input"],
              ["weight_kg", "Weight (kg)", "profile-weight-input"],
            ].map(([k, label, tid]) => (
              <div key={k}>
                <Label className="text-xs font-bold uppercase tracking-[0.15em] text-moss">
                  {label}
                </Label>
                <Input
                  data-testid={tid}
                  type="number"
                  min="1"
                  step="0.1"
                  required
                  value={form[k]}
                  onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
                  className="mt-1.5 bg-white border-[#E8E3D9]"
                />
              </div>
            ))}
          </div>

          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.15em] text-moss">Sex</Label>
            <div className="mt-1.5 flex gap-2">
              {["female", "male"].map((s) => (
                <Button
                  key={s}
                  type="button"
                  data-testid={`profile-sex-${s}`}
                  onClick={() => setForm((f) => ({ ...f, sex: s }))}
                  className={`h-9 rounded-full px-5 text-sm capitalize transition-colors duration-200 ${
                    form.sex === s
                      ? "bg-forest text-sand hover:bg-forest-hover"
                      : "bg-surface text-stone-grey hover:bg-surface-hover hover:text-deep-olive"
                  }`}
                >
                  {s}
                </Button>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.15em] text-moss">
              Activity level
            </Label>
            <div className="mt-1.5 space-y-1.5">
              {ACTIVITY.map(([key, desc]) => (
                <button
                  key={key}
                  type="button"
                  data-testid={`profile-activity-${key}`}
                  onClick={() => setForm((f) => ({ ...f, activity_level: key }))}
                  className={`flex w-full items-center gap-3 rounded-lg border px-4 py-2.5 text-left transition-colors duration-200 ${
                    form.activity_level === key
                      ? "border-forest bg-forest/10"
                      : "border-[#E8E3D9] bg-white hover:bg-surface"
                  }`}
                >
                  <span className="font-heading text-sm font-medium capitalize text-deep-olive">
                    {key}
                  </span>
                  <span className="ml-auto text-xs text-stone-grey">{desc}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3 rounded-lg border border-[#E8E3D9] bg-white px-4 py-3">
            <div className="mr-auto">
              <p className="font-heading text-sm font-medium text-deep-olive">
                Dinner catch-up email
              </p>
              <p className="text-xs text-stone-grey">
                A daily 5pm reminder if you are behind your target.
              </p>
            </div>
            <Switch
              data-testid="profile-reminders-switch"
              checked={form.reminders_enabled}
              onCheckedChange={(v) => setForm((f) => ({ ...f, reminders_enabled: v }))}
            />
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              data-testid="profile-cancel-button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              className="h-10 rounded-full px-5 text-stone-grey hover:bg-surface-hover hover:text-deep-olive transition-colors duration-200"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="profile-save-button"
              disabled={saving}
              className="h-10 rounded-full bg-forest px-6 text-sand hover:bg-forest-hover transition-colors duration-200"
            >
              {saving ? "Calculating…" : "Save & calculate"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
